import { derivePortalBreadcrumbs, type PortalBreadcrumb } from "@lifter/hub-ui/browser-shared";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import { useBrandedDocumentTitle } from "../../auth/instance-branding";

interface PageIdentityContextValue {
  breadcrumbs: PortalBreadcrumb[];
  registerBreadcrumbLabel(href: string, label: string): () => void;
  publishTitle(title: string): void;
}

interface PublishedBreadcrumbLabels {
  pathname: string;
  labels: ReadonlyMap<string, { label: string; owner: symbol }>;
}

const PageIdentityContext = createContext<PageIdentityContextValue | null>(null);

export function PageIdentityProvider({
  children,
  pathname,
}: {
  children: ReactNode;
  pathname: string;
}) {
  const routeKey = pathname;
  const [published, setPublished] = useState<{ routeKey: string; title: string } | null>(null);
  const [publishedBreadcrumbs, setPublishedBreadcrumbs] =
    useState<PublishedBreadcrumbLabels | null>(null);
  const derivedBreadcrumbs = useMemo(() => derivePortalBreadcrumbs(pathname), [pathname]);
  const isNotFound =
    derivedBreadcrumbs.length === 1 && derivedBreadcrumbs[0]?.label === "Page not found";
  const baseBreadcrumbs = isNotFound
    ? [{ href: pathname, label: "Not found" }]
    : derivedBreadcrumbs;
  const page = isNotFound
    ? "Page not found"
    : published?.routeKey === routeKey
      ? published.title
      : (baseBreadcrumbs.at(-1)?.label ?? "Workspace");
  // The tab carries the tenant's name, not the vendor's, on every route the
  // portal owns.
  useBrandedDocumentTitle(page);
  const breadcrumbs = useMemo(
    () =>
      baseBreadcrumbs.map((crumb) => ({
        ...crumb,
        label:
          publishedBreadcrumbs?.pathname === pathname
            ? (publishedBreadcrumbs.labels.get(crumb.href)?.label ?? crumb.label)
            : crumb.label,
      })),
    [baseBreadcrumbs, pathname, publishedBreadcrumbs],
  );
  const publishTitle = useCallback(
    (nextTitle: string) => setPublished({ routeKey, title: nextTitle }),
    [routeKey],
  );
  const registerBreadcrumbLabel = useCallback(
    (href: string, label: string) => {
      if (!baseBreadcrumbs.some((crumb) => crumb.href === href)) return () => undefined;
      const owner = Symbol(href);
      setPublishedBreadcrumbs((current) => {
        const labels =
          current?.pathname === pathname
            ? new Map(current.labels)
            : new Map<string, { label: string; owner: symbol }>();
        labels.set(href, { label, owner });
        return { pathname, labels };
      });
      return () => {
        setPublishedBreadcrumbs((current) => {
          if (current?.pathname !== pathname || current.labels.get(href)?.owner !== owner) {
            return current;
          }
          const labels = new Map(current.labels);
          labels.delete(href);
          return { pathname, labels };
        });
      };
    },
    [baseBreadcrumbs, pathname],
  );

  return (
    <PageIdentityContext.Provider value={{ breadcrumbs, publishTitle, registerBreadcrumbLabel }}>
      {children}
    </PageIdentityContext.Provider>
  );
}

function textFromNode(node: ReactNode): string {
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(textFromNode).join("");
  if (node && typeof node === "object" && "props" in node) {
    return textFromNode((node.props as { children?: ReactNode }).children);
  }
  return "";
}

export function usePageIdentity(title: ReactNode): void {
  const identity = useContext(PageIdentityContext);
  const text = textFromNode(title).replace(/\s+/g, " ").trim();
  useEffect(() => {
    if (text) identity?.publishTitle(text);
  }, [identity?.publishTitle, text]);
}

export function useBreadcrumbLabel(href: string, label: ReactNode | undefined): void {
  const identity = useContext(PageIdentityContext);
  const text = textFromNode(label).replace(/\s+/g, " ").trim();
  useLayoutEffect(() => {
    if (!text) return;
    return identity?.registerBreadcrumbLabel(href, text);
  }, [href, identity?.registerBreadcrumbLabel, text]);
}

export function useShellPageIdentity(): Pick<PageIdentityContextValue, "breadcrumbs"> {
  const identity = useContext(PageIdentityContext);
  if (!identity) return { breadcrumbs: [] };
  return identity;
}
